import { Text, DateField, RichText, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import React, { FC } from 'react';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import ctaTypes from '../../enums/CTA.enums';
import useEEActive from '../../hooks/useEEActive';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import { targetLinkdsk } from '../../utils/targetDskLink';
import CTA from '../@shared/cta';
import DictionaryTerm from '../@shared/dictionaryTerm';
import RoutableNotEditableLink from '../@shared/routableNotEditableLink';
import SocialMediaBar from '../@shared/socialMediaBar';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import NewsDetailMedia from './components/media';
import NewsDetailMediaListItem from './components/newsDetailMediaListItem';
import { TNewsDetailProps } from './types/newsDetail.types';
import { NewsDetailModel } from './models/newsDetail.model';
import useFormatDate from '../../hooks/useFormatDate';

const NewsDetail: FC<TNewsDetailProps> = (props) => {
  const model = new NewsDetailModel(props);
  const isEE = useEEActive();
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['tbl-p']})`);
  const formattedDate = useFormatDate(model.newsDate?.value);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  return (
    <section className="news-detail__wrapper">
      <div className="news-detail">
        <div className="news-detail__header">
          {model?.eyebrow && <Text field={model.eyebrow} tag="div" className={'news-detail__eyebrow'} />}
          {model?.title && <Text field={model.title} tag={model.headingTag} className={'news-detail__title'} />}
          {model?.newsDate && <DateField field={model.newsDate} tag="div" className="news-detail__date" render={() => formattedDate} />}

          {model.socialMediaBarProps && (
            <SocialMediaBar {...model.socialMediaBarProps} useAnalytics={true} extraClassName="news-detail__social-media-bar" />
          )}
        </div>

        <div className={`news-detail__media-wrapper ${!model?.mediaExists ? 'news-detail__media--no-media' : ''}`}>
          <NewsDetailMedia
            showVideo={model?.showVideo}
            desktopImage={model?.desktopImage}
            mobileImage={model?.mobileImage}
            video={model?.video}
            isMediaExists={model?.mediaExists}
          />
        </div>

        {model.categories?.length > 0 && (
          <div className="news-detail__categories-wrapper">
            <span className="news-detail__categories-title">
              <DictionaryTerm keyword="cnhi-news-categories" />
            </span>
            <div className="news-detail__categories-tags-wrapper">
              {model?.categories?.map((category) => (
                <RoutableNotEditableLink key={category?.id} href={category?.href} className="news-detail__categories-tag--link">
                  <span className="news-detail__categories-tag--name">{category?.name}</span>
                </RoutableNotEditableLink>
              ))}
            </div>
          </div>
        )}

        {model.detailedDescription && (
          <RichText tag="div" field={model.detailedDescription} className={'news-detail__description rich-text color-primary-link'} />
        )}

        {model.mediaList.map((mediaItem) => (
          <NewsDetailMediaListItem mediaItem={mediaItem} key={mediaItem.id} />
        ))}

        {(isEE || model.showCtaWrapper) && (
          <div className="news-detail__cta-wrapper">
            {(isEE || model.showPrimaryCta) && (
              <CTA
                type={ctaTypes.primaryRed}
                extraClassName="news-detail__cta-primary"
                field={model.primaryCta}
                target={targetLinkdsk(model?.primaryCta, isDesktop)}
              />
            )}
            {(isEE || model.showSecondaryCta) && (
              <CTA
                type={ctaTypes.secondaryRed}
                field={model.secondaryCta}
                target={targetLinkdsk(model?.secondaryCta, isDesktop)}
                extraClassName={'news-detail__cta-secondary'}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default withSitecoreContext()(NewsDetail);
